// /scramble/:slug — the live golf scramble scorecard.
//
// A standalone, account-free page: anyone with the unguessable slug can pick a
// team and log strokes, and every open device sees updates live via Firestore
// onSnapshot. Ported from the original standalone HTML prototype into React.
// All storage goes through scrambleDb.js; there is no tie-in to ClassBoon data.
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  HOLES,
  TEAM_IDS,
  subscribeRound,
  writeScore,
  writePar,
  writeTeamMeta,
  resetScores,
} from "./scrambleDb";
import "./scramble.css";

const TEAM_COLORS = { t1: "#2E6B3C", t2: "#C1432B", t3: "#9C7A22" };

// Tally a team's holes played, total strokes, and score-to-par so far.
function holesOf(strokes, pars) {
  let thru = 0;
  let total = 0;
  let parSum = 0;
  for (let h = 1; h <= HOLES; h++) {
    const v = strokes[h];
    if (typeof v === "number" && v > 0) {
      thru++;
      total += v;
      parSum += pars[h] || 4;
    }
  }
  return { thru, total, toPar: total - parSum };
}

function fmtToPar(n, thru) {
  if (thru === 0) return "—";
  if (n === 0) return "E";
  return (n > 0 ? "+" : "") + n;
}

// Load the golf display fonts once (kept out of index.html so the rest of the
// app is untouched). Idempotent — appends the link only if it isn't there yet.
function useScrambleFonts() {
  useEffect(() => {
    const href =
      "https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&family=Archivo:wght@500;600;700;800&display=swap";
    if (document.querySelector(`link[href="${href}"]`)) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    document.head.appendChild(link);
  }, []);
}

export default function ScramblePage() {
  const { slug } = useParams();
  useScrambleFonts();

  // undefined = still loading, null = round doesn't exist, object = loaded.
  const [round, setRound] = useState(undefined);
  const roleKey = `scramble-role-${slug}`;
  const [myTeam, setMyTeam] = useState(() => {
    const saved = window.localStorage.getItem(roleKey);
    return saved === "t1" || saved === "t2" || saved === "t3" || saved === "watch"
      ? saved
      : null;
  });
  const [picking, setPicking] = useState(false);
  const [selectedHole, setSelectedHole] = useState(1);
  const [parEditMode, setParEditMode] = useState(false);
  const [showReset, setShowReset] = useState(false);
  // Which team is currently open in the edit modal ("t1"|"t2"|"t3"), or null.
  const [editTeamId, setEditTeamId] = useState(null);
  const entryRef = useRef(null);

  useEffect(() => {
    const unsub = subscribeRound(
      slug,
      (data) => setRound(data),
      () => setRound((r) => (r === undefined ? null : r)) // degrade, keep any local state
    );
    return unsub;
  }, [slug]);

  const teams = round && round.teams ? round.teams : null;
  const pars = round && round.pars ? round.pars : {};

  useEffect(() => {
    if (round && round.courseName) document.title = round.courseName;
  }, [round]);

  function chooseRole(role) {
    setMyTeam(role);
    window.localStorage.setItem(roleKey, role);
    setPicking(false);
  }

  // Optimistically patch local round state so entry feels instant; the snapshot
  // reconciles with the authoritative value moments later.
  function patchStrokes(teamId, hole, value) {
    setRound((r) => {
      if (!r) return r;
      const t = r.teams[teamId];
      return {
        ...r,
        teams: { ...r.teams, [teamId]: { ...t, strokes: { ...t.strokes, [hole]: value } } },
      };
    });
  }

  function stepScore(delta) {
    if (!myTeam || myTeam === "watch" || !teams) return;
    const hole = selectedHole;
    const current = teams[myTeam].strokes[hole];
    // First tap on an unscored hole seeds at par (either button); after that,
    // +/− nudges from the current value.
    let next = typeof current === "number" ? current + delta : pars[hole] || 4;
    if (next < 1) next = 1;
    if (next > 15) next = 15;
    patchStrokes(myTeam, hole, next);
    writeScore(slug, myTeam, hole, next).catch(() => {});
  }

  function saveTeam(teamId, name, members) {
    setRound((r) =>
      r ? { ...r, teams: { ...r.teams, [teamId]: { ...r.teams[teamId], name, members } } } : r
    );
    writeTeamMeta(slug, teamId, name, members).catch(() => {});
    setEditTeamId(null);
  }

  function changePar(hole, dir) {
    let next = (pars[hole] || 4) + dir;
    if (next < 3) next = 3;
    if (next > 6) next = 6;
    setRound((r) => (r ? { ...r, pars: { ...r.pars, [hole]: next } } : r));
    writePar(slug, hole, next).catch(() => {});
  }

  function doReset() {
    setRound((r) => {
      if (!r) return r;
      const cleared = {};
      TEAM_IDS.forEach((id) => {
        cleared[id] = { ...r.teams[id], strokes: {} };
      });
      return { ...r, teams: cleared };
    });
    setSelectedHole(1);
    resetScores(slug).catch(() => {});
    setShowReset(false);
  }

  function jumpToEntry(teamId, hole) {
    if (myTeam !== teamId) return;
    setSelectedHole(hole);
    if (entryRef.current) entryRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  // ---- loading / missing states ----
  if (round === undefined) {
    return (
      <div className="scramble-root">
        <div className="center-state">
          <p>Loading scorecard…</p>
        </div>
      </div>
    );
  }
  if (round === null) {
    return (
      <div className="scramble-root">
        <div className="center-state">
          <h2>Round not found</h2>
          <p>This scramble link doesn’t point to a round. Double-check the URL, or start a fresh one at /scramble/new.</p>
        </div>
      </div>
    );
  }

  const showPicker = !myTeam || picking;

  return (
    <div className="scramble-root">
      <Header round={round} myTeam={myTeam} teams={teams} onChange={() => setPicking(true)} />

      {showPicker ? (
        <Picker teams={teams} pars={pars} onPick={chooseRole} onEdit={setEditTeamId} />
      ) : (
        <main>
          <Leaderboard teams={teams} pars={pars} myTeam={myTeam} />

          <div ref={entryRef}>
            <EntryCard
              myTeam={myTeam}
              teams={teams}
              pars={pars}
              selectedHole={selectedHole}
              setSelectedHole={setSelectedHole}
              onStep={stepScore}
              onEditTeam={() => setEditTeamId(myTeam)}
            />
          </div>

          <div className="card-section">
            <div className="section-head">
              <h3>Full scorecard</h3>
              <button
                className="par-edit-toggle"
                type="button"
                onClick={() => setParEditMode((v) => !v)}
              >
                {parEditMode ? "Done" : "Edit pars"}
              </button>
            </div>
            <div style={{ overflowX: "auto" }}>
              <ScoreTable
                teams={teams}
                pars={pars}
                myTeam={myTeam}
                parEditMode={parEditMode}
                onChangePar={changePar}
                onJump={jumpToEntry}
              />
            </div>
          </div>

          <div className="reset-row">
            <button className="reset-btn" type="button" onClick={() => setShowReset(true)}>
              Reset scores for a new round
            </button>
          </div>

          <footer className="note">
            Tap a hole on your team’s card to jump the score entry above it. Adjust “Edit pars”
            to match your printed card.
          </footer>
        </main>
      )}

      {showReset && <ResetModal onCancel={() => setShowReset(false)} onConfirm={doReset} />}
      {editTeamId && teams && teams[editTeamId] && (
        <TeamEditModal
          team={teams[editTeamId]}
          onCancel={() => setEditTeamId(null)}
          onSave={(name, members) => saveTeam(editTeamId, name, members)}
        />
      )}
    </div>
  );
}

function Header({ round, myTeam, teams, onChange }) {
  let roleChip = null;
  if (myTeam) {
    const watching = myTeam === "watch";
    const label = watching
      ? "Watching only"
      : "Scoring for " + ((teams[myTeam] && teams[myTeam].name) || "your team");
    const color = watching ? "var(--ink-soft)" : TEAM_COLORS[myTeam];
    roleChip = (
      <div className="role-row">
        <div className="role-chip">
          <span className="dot" style={{ background: color }} />
          <span className="txt">{label}</span>
        </div>
        <button className="change-btn" type="button" onClick={onChange}>
          Change
        </button>
      </div>
    );
  }
  return (
    <header className="top">
      <div className="course-row">
        <div>
          <div className="course-name">{round.courseName}</div>
          {round.courseSub ? <div className="course-sub">{round.courseSub}</div> : null}
        </div>
        <span className="live-pill">
          <span className="live-dot" />
          Live
        </span>
      </div>
      {roleChip}
    </header>
  );
}

function Picker({ teams, pars, onPick, onEdit }) {
  return (
    <div className="picker">
      <h2>Who’s scoring?</h2>
      <p>Name your teams and players, then pick your team to enter strokes on this phone. Everyone sees every team’s score update live.</p>
      <div className="picker-grid">
        {TEAM_IDS.map((id) => {
          const t = teams[id];
          const roster = Array.isArray(t.members) ? t.members : [];
          const h = holesOf(t.strokes, pars);
          const meta = h.thru ? `${h.thru} holes logged` : "No scores yet";
          return (
            <div key={id} className="team-pick-btn">
              <button className="team-pick-main" type="button" onClick={() => onPick(id)}>
                <span className="name">{t.name}</span>
                {roster.length > 0 && <span className="roster">{roster.join(" · ")}</span>}
                <span className="meta">{meta}</span>
              </button>
              <button className="team-edit-link" type="button" onClick={() => onEdit(id)}>
                Edit
              </button>
              <span className="arrow" aria-hidden="true">→</span>
            </div>
          );
        })}
      </div>
      <button className="watch-btn" type="button" onClick={() => onPick("watch")}>
        Just watching — show me the leaderboard
      </button>
    </div>
  );
}

function Leaderboard({ teams, pars, myTeam }) {
  const rows = TEAM_IDS.map((id) => ({
    id,
    name: teams[id].name,
    members: Array.isArray(teams[id].members) ? teams[id].members : [],
    h: holesOf(teams[id].strokes, pars),
  }));
  const ranked = rows.filter((r) => r.h.thru > 0).slice().sort((a, b) => a.h.total - b.h.total);
  const rankOf = {};
  ranked.forEach((r, i) => {
    rankOf[r.id] = i + 1;
  });
  return (
    <div className="leaderboard">
      {rows.map((r) => {
        const rank = rankOf[r.id];
        const cls = `lb-tile${rank ? " rank-" + rank : ""}${myTeam === r.id ? " mine" : ""}`;
        return (
          <div key={r.id} className={cls}>
            {rank ? (
              <span className="rank-badge">{rank === 1 ? "1ST" : rank === 2 ? "2ND" : "3RD"}</span>
            ) : null}
            <div className="lb-name">{r.name}</div>
            {r.members.length > 0 && <div className="lb-members">{r.members.join(" · ")}</div>}
            <div className="lb-total num">{r.h.thru ? r.h.total : "—"}</div>
            <div className="lb-sub num">
              {r.h.thru ? `${fmtToPar(r.h.toPar, r.h.thru)} · thru ${r.h.thru}` : "not started"}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function EntryCard({ myTeam, teams, pars, selectedHole, setSelectedHole, onStep, onEditTeam }) {
  if (!myTeam || myTeam === "watch") {
    return (
      <div className="watch-card">
        You’re watching live scores. Pick a team above if you want to enter strokes from this phone.
      </div>
    );
  }
  const team = teams[myTeam];
  const roster = Array.isArray(team.members) ? team.members : [];
  const hole = selectedHole;
  const par = pars[hole] || 4;
  const val = team.strokes[hole];
  const diff = typeof val === "number" ? val - par : null;

  let diffLabel;
  if (diff === null) {
    diffLabel = <div className="stroke-vs" style={{ color: "var(--ink-soft)" }}>Not entered</div>;
  } else {
    const cls = diff < 0 ? "vs-under" : diff > 0 ? "vs-over" : "vs-even";
    const txt = diff === 0 ? "Par" : diff < 0 ? `${diff} (under)` : `+${diff} (over)`;
    diffLabel = <div className={`stroke-vs ${cls}`}>{txt}</div>;
  }

  return (
    <div className="entry-card">
      <div className="entry-top">
        <span className="entry-title">{team.name} · Hole {hole}</span>
        <button className="change-btn" type="button" onClick={onEditTeam}>
          Edit team
        </button>
      </div>
      {roster.length > 0 && <div className="entry-roster">{roster.join(" · ")}</div>}
      <div className="hole-strip">
        {Array.from({ length: HOLES }, (_, i) => i + 1).map((h) => {
          const filled = typeof team.strokes[h] === "number";
          const cls = `hole-dot${filled ? " filled" : ""}${h === hole ? " active" : ""}`;
          return (
            <button key={h} type="button" className={cls} onClick={() => setSelectedHole(h)}>
              {h}
            </button>
          );
        })}
      </div>
      <div className="stroke-zone">
        <button className="stepper-btn" type="button" aria-label="Subtract stroke" onClick={() => onStep(-1)}>
          −
        </button>
        <div className="stroke-mid">
          <div className="stroke-par">Par {par}</div>
          <div className="stroke-num num">{typeof val === "number" ? val : "–"}</div>
          {diffLabel}
        </div>
        <button className="stepper-btn" type="button" aria-label="Add stroke" onClick={() => onStep(1)}>
          +
        </button>
      </div>
      <div className="entry-nav">
        <button
          className="nav-btn"
          type="button"
          disabled={hole <= 1}
          onClick={() => setSelectedHole(hole - 1)}
        >
          ← Hole {hole - 1}
        </button>
        <button
          className="nav-btn primary"
          type="button"
          disabled={hole >= HOLES}
          onClick={() => setSelectedHole(hole + 1)}
        >
          Hole {hole + 1} →
        </button>
      </div>
    </div>
  );
}

function ScoreTable({ teams, pars, myTeam, parEditMode, onChangePar, onJump }) {
  const holeRow = (h) => {
    const par = pars[h] || 4;
    return (
      <tr key={h}>
        <td>{h}</td>
        {parEditMode ? (
          <td className="par-cell">
            <span className="par-stepper">
              <button type="button" onClick={() => onChangePar(h, -1)}>−</button>
              <span className="num">{par}</span>
              <button type="button" onClick={() => onChangePar(h, 1)}>+</button>
            </span>
          </td>
        ) : (
          <td className="par-cell num">{par}</td>
        )}
        {TEAM_IDS.map((id) => {
          const v = teams[id].strokes[h];
          const mine = myTeam === id;
          return (
            <td
              key={id}
              className={`num${mine ? " mine" : ""}${mine ? " jump-cell" : ""}`}
              onClick={() => onJump(id, h)}
            >
              {typeof v === "number" ? v : "–"}
            </td>
          );
        })}
      </tr>
    );
  };

  const subtotalRow = (label, from, to) => {
    let parSum = 0;
    for (let h = from; h <= to; h++) parSum += pars[h] || 4;
    return (
      <tr className="subtotal" key={label}>
        <td>{label}</td>
        <td className="num">{parSum}</td>
        {TEAM_IDS.map((id) => {
          const s = teams[id].strokes;
          let sum = 0;
          let any = false;
          for (let h = from; h <= to; h++) {
            if (typeof s[h] === "number") {
              sum += s[h];
              any = true;
            }
          }
          return <td key={id} className="num">{any ? sum : "–"}</td>;
        })}
      </tr>
    );
  };

  const totalRow = () => {
    let parSum = 0;
    for (let h = 1; h <= HOLES; h++) parSum += pars[h] || 4;
    return (
      <tr className="total">
        <td>Total</td>
        <td className="num">{parSum}</td>
        {TEAM_IDS.map((id) => {
          const h = holesOf(teams[id].strokes, pars);
          return <td key={id} className="num">{h.thru ? h.total : "–"}</td>;
        })}
      </tr>
    );
  };

  const front = [];
  for (let h = 1; h <= 9; h++) front.push(holeRow(h));
  const back = [];
  for (let h = 10; h <= 18; h++) back.push(holeRow(h));

  return (
    <table className="card">
      <thead>
        <tr>
          <th>Hole</th>
          <th>Par</th>
          {TEAM_IDS.map((id) => (
            <th key={id} style={{ color: TEAM_COLORS[id] }}>
              {teams[id].name}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {front}
        {subtotalRow("Out", 1, 9)}
        {back}
        {subtotalRow("In", 10, 18)}
        {totalRow()}
      </tbody>
    </table>
  );
}

function TeamEditModal({ team, onCancel, onSave }) {
  const [name, setName] = useState(team.name || "");
  const [members, setMembers] = useState(() =>
    Array.isArray(team.members) && team.members.length ? [...team.members] : [""]
  );

  const setMember = (i, val) => setMembers((m) => m.map((x, idx) => (idx === i ? val : x)));
  const addMember = () => setMembers((m) => [...m, ""]);
  const removeMember = (i) => setMembers((m) => (m.length === 1 ? [""] : m.filter((_, idx) => idx !== i)));

  const save = () => {
    const cleaned = members.map((s) => s.trim()).filter(Boolean);
    onSave(name.trim() || team.name, cleaned);
  };

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="modal-card">
        <h3>Edit team</h3>
        <label className="field-label" htmlFor="team-name">Team name</label>
        <input
          id="team-name"
          className="text-input"
          type="text"
          value={name}
          maxLength={40}
          onChange={(e) => setName(e.target.value)}
          placeholder="Team name"
        />
        <label className="field-label" style={{ marginTop: 14 }}>Players</label>
        {members.map((m, i) => (
          <div className="member-row" key={i}>
            <input
              className="text-input"
              type="text"
              value={m}
              maxLength={40}
              onChange={(e) => setMember(i, e.target.value)}
              placeholder={`Player ${i + 1}`}
            />
            <button
              className="member-remove"
              type="button"
              aria-label="Remove player"
              onClick={() => removeMember(i)}
            >
              ×
            </button>
          </div>
        ))}
        <button className="add-player-btn" type="button" onClick={addMember}>
          + Add player
        </button>
        <div className="modal-actions" style={{ marginTop: 18 }}>
          <button className="modal-cancel" type="button" onClick={onCancel}>
            Cancel
          </button>
          <button className="modal-confirm-green" type="button" onClick={save}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function ResetModal({ onCancel, onConfirm }) {
  return (
    <div
      className="modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="modal-card">
        <h3>Reset all scores?</h3>
        <p>
          This clears every team’s strokes for all 18 holes so you can start a new round. Team
          names and pars stay as they are. This can’t be undone.
        </p>
        <div className="modal-actions">
          <button className="modal-cancel" type="button" onClick={onCancel}>
            Cancel
          </button>
          <button className="modal-confirm" type="button" onClick={onConfirm}>
            Reset scores
          </button>
        </div>
      </div>
    </div>
  );
}
